import React, { useEffect, useMemo, useState } from 'react'
import { create } from 'zustand'
import { Copy, TriangleAlert } from 'lucide-react'
import { mergeClasses } from '@/helpers/styling'
import { Button, MiniButton } from '@/components/ui-primitives/small'
import { CodeEditor } from '@/components/editor/code-editor'
import { useSparqlQuery } from '@/helpers/use-sparql-query'
import { OpaqueSparqlResults } from '@/helpers/sparqlResultHelpers'
import { placeholderTopic, WidgetQueryStatus } from '@/components/editor/widget-editor/query-status'

const PREVIEW_ROW_LIMIT = 50
const MAX_ROW_LIMIT = 2000

const RESULT_STYLE = {
  variable: "text-[#a03d00]",
  iri: "text-[#1f6f5c]",
  errorBox: "border-[#f3c4c4]",
  errorHeader: "bg-[#fde8e8] text-[#9b1c1c]",
  errorMark: "bg-[#fbd5d5] text-[#9b1c1c] underline decoration-wavy decoration-[#c81e1e]",
  waitingBox: "border-[#f0dca8] bg-[#fffaf0] text-[#6b4600]",
  testBanner: "bg-[#efeafe] text-[#352d73]",
}

const NUMERIC_DATATYPE = /XMLSchema#(integer|int|long|short|decimal|double|float|nonNegativeInteger|positiveInteger)$/

type Cell = { type: string, value: string, datatype?: string, 'xml:lang'?: string }

/**
 * Results of a widget's query in the configurator: what the widget itself received,
 * or a one-off test run with placeholder values filled in by hand.
 */
export function QueryResults(props: {
  status: WidgetQueryStatus | null
  /** Inside the query card, which frames it and names it in its tab */
  bare?: boolean
}) {
  const { status } = props
  const [ view, setView ] = useState<"table" | "json">("table")
  const [ testQuery, setTestQuery ] = useState<{ query: string, label: string } | null>(null)
  // kept outside the form, so the values stay after a test run and when the card changes size
  const testValues = useTestValuesStore(st => st[status?.template ?? ""] ?? NO_TEST_VALUES)
  const setTestValues = (values: Record<string, string>) => {
    if (status?.template) useTestValuesStore.setState({ [status.template]: values })
  }
  const waitingForPlaceholders = status?.status === "disabled" && status.missingPlaceholders.length > 0

  useEffect(function endTestRunWhenQueryChanges() {
    setTestQuery(null)
  }, [ status?.template ])

  const testResult = useSparqlQuery({
    enabled: Boolean(testQuery && status),
    query: testQuery?.query ?? "# disabled",
    sparqlEndpoint: status?.endpoint ?? "",
    source: "configurator-test-run",
  })

  const shown = testQuery
    ? {
      state: testResult.status,
      data: testResult.data ?? null,
      error: testResult.error?.message ?? null,
      errorDetail: null,
      query: testQuery.query,
    } : {
      state: status?.status,
      data: status?.data ?? null,
      error: status?.error ?? null,
      errorDetail: status?.errorDetail ?? null,
      query: status?.resolvedQuery ?? null,
    }
  const rowCount = shown.data?.results.bindings.length ?? 0
  const hasRows = shown.state === "success" && rowCount > 0

  return (
    <section aria-label="Query results"
             className={mergeClasses("flex flex-col min-h-0 overflow-hidden bg-white0", {
               "rounded-md border-[1px] border-border max-h-[55vh]": !props.bare,
               "h-full": props.bare,
             })}>
      <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border bg-muted/40">
        {!props.bare && <h3 className="m-0 shrink-0 font-exo text-[13px] font-semibold">Results</h3>}
        {shown.state === "success" && (
          <span className="min-w-0 text-xs text-muted-foreground truncate">
            {rowCount} rows · {shown.data?.head.vars.length ?? 0} variables
          </span>
        )}
        <div className="flex-grow" />
        <div className="flex shrink-0 items-center gap-1">
          {hasRows && (
            <>
              <div role="group" aria-label="Result view" className="flex rounded-md border-[1px] border-border overflow-hidden text-xs">
                {(["table", "json"] as const).map(option => (
                  <button key={option}
                          type="button"
                          aria-pressed={view === option}
                          onClick={() => setView(option)}
                          className={mergeClasses("px-2 py-0.5 capitalize text-muted-foreground", {
                            "bg-white0 font-semibold text-[#51479e]": view === option,
                          })}>
                    {option === "json" ? "JSON" : "Table"}
                  </button>
                ))}
              </div>
              <MiniButton title="Copy as CSV" onClick={() => void navigator.clipboard.writeText(toCsv(shown.data!))}>
                <Copy size={13} />
              </MiniButton>
            </>
          )}
        </div>
      </div>

      {testQuery && (
        <div className={mergeClasses("flex items-center gap-2 px-2.5 py-1.5 text-xs", RESULT_STYLE.testBanner)}>
          <span className="truncate">Test run with {testQuery.label}. The widget itself is not affected.</span>
          <button type="button" className="ml-auto underline" onClick={() => setTestQuery(null)}>Clear</button>
        </div>
      )}

      <div className={mergeClasses("flex flex-col min-h-0 overflow-auto", { "flex-1": props.bare })}>
        {status && waitingForPlaceholders && (
          <WaitingState status={status}
                        values={testValues}
                        onValuesChange={setTestValues}
                        hasRun={Boolean(testQuery)}
                        onTestRun={setTestQuery} />
        )}
        {!status ? (
          <Message>This widget is not rendered on the page right now, so there are no results to show.</Message>
        ) : waitingForPlaceholders && !testQuery ? null : shown.state === "pending" && !testQuery ? (
          <Message>Running…</Message>
        ) : shown.state === "pending" ? (
          <Message>Running test…</Message>
        ) : shown.state === "error" ? (
          <ErrorState error={shown.error} errorDetail={shown.errorDetail} query={shown.query} />
        ) : shown.state === "disabled" ? (
          <Message>Not running: the widget is hidden or in an inactive tab.</Message>
        ) : !hasRows ? (
          <Message>
            <span className="font-semibold text-foreground">0 rows.</span>{" "}
            The query ran fine but matched nothing.
            {Boolean(shown.data?.head.vars.length) && (
              <> Variables: <Variables vars={shown.data!.head.vars} /></>
            )}
          </Message>
        ) : view === "json" ? (
          <div className="flex flex-col p-2 min-h-0">
            <CodeEditor mode="json" readonly value={JSON.stringify(shown.data, null, 2)} minHeight="240px" />
          </div>
        ) : (
          <ResultsTable data={shown.data!} query={shown.query} />
        )}
      </div>
    </section>
  )
}

function ResultsTable(props: { data: OpaqueSparqlResults, query: string | null }) {
  const [ showAll, setShowAll ] = useState(false)
  const prefixes = useMemo(() => parsePrefixes(props.query), [ props.query ])
  const { vars } = props.data.head
  const bindings = props.data.results.bindings
  const visible = bindings.slice(0, showAll ? MAX_ROW_LIMIT : PREVIEW_ROW_LIMIT)

  return (
    <>
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 bg-white0">
          <tr className="text-left font-mono">
            <th className="w-8 px-2.5 py-1.5 border-b border-border font-medium text-muted-foreground">#</th>
            {vars.map(v => (
              <th key={v} className={mergeClasses("px-2.5 py-1.5 border-b border-border font-medium whitespace-nowrap", RESULT_STYLE.variable)}>
                ?{v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((binding, i) => (
            <tr key={i} className="hover:bg-muted/40">
              <td className="px-2.5 py-1 border-b border-border/60 text-muted-foreground tabular-nums">{i + 1}</td>
              {vars.map(v => (
                <ResultCell key={v} cell={(binding as Record<string, Cell | undefined>)[v]} prefixes={prefixes} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {bindings.length > PREVIEW_ROW_LIMIT && (
        <div className="flex items-center gap-2 px-2.5 py-1.5 border-t border-border text-xs text-muted-foreground">
          Showing {visible.length} of {bindings.length}
          {!showAll && (
            <button type="button" className="text-[#51479e] underline" onClick={() => setShowAll(true)}>Show all</button>
          )}
        </div>
      )}
    </>
  )
}

function ResultCell(props: { cell: Cell | undefined, prefixes: [ string, string ][] }) {
  const { cell } = props
  if (!cell) return <td className="px-2.5 py-1 border-b border-border/60" />
  const isNumeric = Boolean(cell.datatype && NUMERIC_DATATYPE.test(cell.datatype))
  return (
    <td className={mergeClasses("px-2.5 py-1 border-b border-border/60 max-w-[24rem] truncate", {
      "text-right tabular-nums": isNumeric,
    })} title={cell.type === "uri" ? cell.value : cell.datatype}>
      {cell.type === "uri" ? (
        <span className={mergeClasses("font-mono", RESULT_STYLE.iri)}>{compactIri(cell.value, props.prefixes)}</span>
      ) : cell.type === "bnode" ? (
        <span className="font-mono text-muted-foreground">_:{cell.value}</span>
      ) : (
        <>
          {cell.value}
          {cell['xml:lang'] && <span className="ml-1 text-[11px] text-muted-foreground">@{cell['xml:lang']}</span>}
        </>
      )}
    </td>
  )
}

function ErrorState(props: { error: string | null, errorDetail: string | null, query: string | null }) {
  const message = props.errorDetail ?? props.error ?? "The query failed."
  const location = findErrorLocation(message, props.query)
  return (
    <div className={mergeClasses("m-2.5 rounded-md border-[1px] overflow-hidden", RESULT_STYLE.errorBox)}>
      <div className={mergeClasses("flex items-center gap-2 px-2.5 py-1.5 text-[13px] font-semibold", RESULT_STYLE.errorHeader)}>
        <TriangleAlert size={14} /> Query failed
      </div>
      <div className="flex flex-col gap-2 p-2.5 text-xs">
        <div className="font-mono whitespace-pre-wrap break-words max-h-40 overflow-auto">{message}</div>
        {location && (
          <div className="font-mono whitespace-pre rounded-sm bg-[#fff7f7] px-2 py-1 overflow-x-auto">
            <span className="text-muted-foreground mr-3">{location.line}</span>
            {location.before}
            <span className={RESULT_STYLE.errorMark}>{location.token}</span>
            {location.after}
          </div>
        )}
      </div>
    </div>
  )
}

/** Placeholder values typed for a test run, by query template */
const useTestValuesStore = create<Record<string, Record<string, string>>>()(() => ({}))
const NO_TEST_VALUES: Record<string, string> = {}

function WaitingState(props: {
  status: WidgetQueryStatus
  values: Record<string, string>
  onValuesChange: (values: Record<string, string>) => void
  /** Whether a test run with these values has been done, which the results below show */
  hasRun: boolean
  onTestRun: (run: { query: string, label: string }) => void
}) {
  const { missingPlaceholders } = props.status
  const { values } = props
  const [ stillMissing, setStillMissing ] = useState(false)

  const runOnce = () => {
    const byTopic = Object.fromEntries(missingPlaceholders.map(p => [ placeholderTopic(p), values[p] ?? "" ]))
    const query = props.status.resolveWith(byTopic)
    setStillMissing(!query)
    if (!query) return
    props.onTestRun({
      query,
      label: missingPlaceholders.map(p => `{{${p}}} = ${values[p] ?? ""}`).join(", "),
    })
  }

  return (
    // not a <form>: this renders inside the widget editor's form, and forms cannot nest
    <div className={mergeClasses("m-2.5 flex flex-col gap-2 rounded-md border-[1px] p-2.5 text-xs", RESULT_STYLE.waitingBox)}>
      <div className="text-[13px] font-semibold">Not run yet: waiting for {missingPlaceholders.length > 1 ? "placeholders" : "a placeholder"}</div>
      <div>
        The query runs once {missingPlaceholders.length > 1 ? "these have values" : "this has a value"}, usually published by another widget.
        You can try it with values of your own:
      </div>
      {missingPlaceholders.map(placeholder => (
        <label key={placeholder} className="flex items-center gap-2 m-0">
          <span className="font-mono shrink-0">{`{{${placeholder}}}`}</span>
          <input value={values[placeholder] ?? ""}
                 onChange={e => props.onValuesChange({ ...values, [placeholder]: e.target.value })}
                 onKeyDown={e => {
                   if (e.key !== "Enter") return
                   e.preventDefault()
                   runOnce()
                 }}
                 className="flex-1 min-w-0 h-7 px-2 rounded-md border-[1px] border-[#e8d49c] bg-white0 font-mono text-xs outline-none focus-visible:ring-2 focus-visible:ring-[#51479e]/40" />
        </label>
      ))}
      <div className="flex items-center gap-2">
        <Button variant="purple" onClick={runOnce}>{props.hasRun ? "Run again" : "Run once"}</Button>
        {stillMissing && <span>The query still has unresolved placeholders.</span>}
      </div>
    </div>
  )
}

function Message(props: { children: React.ReactNode }) {
  return <div className="px-2.5 py-3 text-xs text-muted-foreground">{props.children}</div>
}

function Variables(props: { vars: string[] }) {
  return <span className={mergeClasses("font-mono", RESULT_STYLE.variable)}>{props.vars.map(v => `?${v}`).join(" ")}</span>
}

function parsePrefixes(query: string | null): [ string, string ][] {
  if (!query) return []
  return [ ...query.matchAll(/PREFIX\s+([\w-]*):\s*<([^>]+)>/gi) ].map(m => [ m[1], m[2] ])
}

function compactIri(iri: string, prefixes: [ string, string ][]) {
  const match = prefixes.find(([ , namespace ]) => iri.startsWith(namespace))
  return match ? `${match[0]}:${iri.slice(match[1].length)}` : iri
}

/** Endpoints report syntax errors as "... line 18, column 4 ..."; point at that spot in the query. */
function findErrorLocation(message: string, query: string | null) {
  const match = message.match(/line:?\s*(\d+),?\s*column:?\s*(\d+)/i)
  if (!match || !query) return null
  const line = Number(match[1])
  const text = query.split(/\r?\n/)[line - 1]
  if (text == null) return null
  const column = Math.max(0, Number(match[2]) - 1)
  const token = text.slice(column).match(/^\S+/)?.[0] ?? text.charAt(column)
  return {
    line,
    before: text.slice(0, column),
    token,
    after: text.slice(column + token.length),
  }
}

function toCsv(data: OpaqueSparqlResults) {
  const escape = (value: string) => /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
  const { vars } = data.head
  const rows = data.results.bindings.map(binding =>
    vars.map(v => escape((binding as Record<string, Cell | undefined>)[v]?.value ?? "")).join(","))
  return [ vars.join(","), ...rows ].join("\n")
}
